import { css } from "@emotion/react"
import { useContext, useMemo, useState } from "react"
import HealthEffectsMap from "../physicalAssessment/HealthEffectsMap"
import { BodyPartProvider } from "../../context/BodyPart"
import { colors } from "shared-ui"
import EffectsList from "../physicalAssessment/EffectsList"
import FancyDropdown from "../_control/FancyDropdown"
import { SurfaceLayerProvider } from "../../context/SurfaceLayer"
import PitchEffects from "../physicalAssessment/PitchEffects"
import { useIntl } from "react-intl"
import SwitcherSimple from "../_control/SwitcherSimple"
import PreferredViewContext from "../../context/PreferredView"
import { TestAverageLinearGauge } from "shared-ui"
import UnitSystemContext from "../../context/UnitSystem"

export default function AnalysisDataSummary({ analysis, tests, ...props }) {

    const { formatMessage } = useIntl()

    const { preferredView, handlePreferredViewChange, PREFERRED_VIEW_TYPES } = useContext(PreferredViewContext)

    const { unitSystem } = useContext(UnitSystemContext)

    const filterOptions = useMemo(() => [
        { label: formatMessage({ id: 'all' }), value: 'all' },
        { label: formatMessage({ id: 'severeEffect' }), value: 'severe' },
        { label: formatMessage({ id: 'lightEffect' }), value: 'light' },
        { label: formatMessage({ id: 'positive' }), value: 'positive' },
        { label: formatMessage({ id: 'negative' }), value: 'negative' },
    ], [])

    const [filterValue, setFilterValue] = useState(filterOptions[0])

    const [showHealthEffects, showPitchEffects, showToggle] = useMemo(() => {
        let hasHealthEffects = false
        let hasPitchEffects = false

        analysis?.tests?.forEach(test => {
            if (!!test?.testCategory?.hasPhysicalAssessments) {
                if (!hasHealthEffects) hasHealthEffects = true
            }
            if (!!test?.testCategory?.hasPitchAssessments) {
                if (!hasPitchEffects) hasPitchEffects = true
            }
        })

        if (hasHealthEffects && hasPitchEffects) {
            // if test has configuration for both, show a toggle and only one at a time
            switch (preferredView) {
                case PREFERRED_VIEW_TYPES.performance: return [hasHealthEffects, false, true]
                case PREFERRED_VIEW_TYPES.greenkeeping: return [false, hasPitchEffects, true]
                default: return [hasHealthEffects, false, true]
            }
        } else {
            // else show whichever has configuration, if any, and no toggle
            return [hasHealthEffects, hasPitchEffects, false]
        }
    }, [analysis, preferredView, PREFERRED_VIEW_TYPES])

    if (!showHealthEffects && !showPitchEffects) return
    return <div css={style.wrapper} id='analysis-data-summary'>
        <div css={style.grid}>
            {showHealthEffects &&
                <BodyPartProvider >
                    <div css={style.image}>
                        {showToggle &&
                            <SwitcherSimple css={css`justify-self: start;`}
                                checked={!showHealthEffects}
                                onClick={handlePreferredViewChange}
                                unCheckedLabel={formatMessage({ id: 'performance' })}
                                checkedLabel={formatMessage({ id: 'grounds' })}
                                buttonCenter={true}
                            />
                        }
                        <HealthEffectsMap
                            tests={analysis.tests || []}
                            filter={filterValue.value}
                            css={css`
                                width: 22em !important;
                                height: 22em !important;
                                align-self: center;
                            `}
                        />
                        <FancyDropdown
                            value={filterValue}
                            options={filterOptions}
                            onChange={setFilterValue}
                            openUpwards={true}
                            subtle={true}
                            compact={true}
                        />
                    </div>

                    <div css={style.effectsWrapper}>
                        <EffectsList tests={analysis.tests} type={'player'} filter={filterValue.value} />
                    </div>
                </BodyPartProvider>
            }

            {showPitchEffects &&
                <SurfaceLayerProvider >
                    <div css={style.image}>
                        {showToggle &&
                            <SwitcherSimple css={css`justify-self: start; `}
                                checked={!showHealthEffects}
                                onClick={handlePreferredViewChange}
                                unCheckedLabel={formatMessage({ id: 'performance' })}
                                checkedLabel={formatMessage({ id: 'grounds' })}
                                buttonCenter={true}
                            />
                        }
                        <PitchEffects
                            tests={analysis.tests || []}
                            filter={filterValue.value}
                        />

                        <FancyDropdown
                            value={filterValue}
                            options={filterOptions}
                            onChange={setFilterValue}
                            openUpwards={true}
                            subtle={true}
                            compact={true}
                        />
                    </div>

                    <div css={style.effectsWrapper}>
                        <EffectsList tests={analysis.tests} type={'pitch'} filter={filterValue.value} />
                    </div>
                </SurfaceLayerProvider>
            }

            <div css={style.testResults} >
                {tests?.map(t =>
                    <TestAverageLinearGauge
                        key={t.id}
                        test={t}
                        unitSystem={unitSystem}
                        lightMode={false}
                        notApplicableAbbrString={formatMessage({ id: 'notApplicableAbbr' })}
                    />
                )}
            </div>
        </div>
    </div>
}

const style = {
    wrapper: css`
        display: flex; 
        flex-direction: column;
        background: ${colors.eventDark};
        background-image: linear-gradient(170deg, rgba(255,255,255,0) 50%, #02F2FF1C);
        padding: 1em;
        margin-bottom: 1em;
        border-radius: 5px;
        max-width: 3200px;
    `,
    grid: css`
        display: grid;
        grid-template: 1fr / 24em 1fr 1fr;
        gap: 2em;

        @media screen and (max-width: 800px) {
            display: flex; 
            flex-direction: column;
        }
    `,
    testResults: css`
        margin: 0;
        display: grid;
        grid-template-columns: 1fr;
        overflow: hidden;
        place-items: start;
        max-height: 29em;
        overflow-Y: auto;
        user-select: none;

        @media screen and (max-width: 800px) {
            width: 100%;
            padding-left: 0.5em;
        }
    `,
    image: css`
        padding: 1em;
        flex-grow: 1;
        max-width: 24em;
        width: 100%;
        height: 100%;
        min-height: 29em;
        justify-content: space-between;
        display: flex;
        flex-direction: column;
        align-self: start;

        @media screen and (max-width: 800px) {
            place-self: center;
        }
    `,
    effectsWrapper: css`
        padding-left: 1em;
        padding-right: 1em;
        overflow: auto;
        max-height: 29em;

        border-left: 1px solid rgba(255,255,255,0.1);
        border-right: 1px solid rgba(255,255,255,0.1);

        @media screen and (max-width: 800px) {
            border: none;
        }
    `,
}

function filterImpactArray(arr, filter, type) {
    return arr.filter(impact => {

        const isPos = type === 'pitch' ?
            !impact.pitchEffect?.negativeEffect
            : !impact.healthEffect?.negativeEffect
        const isSev = impact.severity === 'severe'

        switch (filter.value) {
            case 'all': return true
            case 'positive': return isPos
            case 'negative': return !isPos
            case 'light': return !isSev
            case 'severe': return isSev
            default: return true
        }
    })
}